import { Button, Input } from "antd";
import queryClient from "api/queryClient.ts";
import {
    useGetOutgoingPostbackMethodsExportQuery,
    useUpdateOutgoingPostbackMethodsMutation,
} from "api/TenantSlug/OutgoingPostback/hooks.ts";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { FC } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { PermissionEnum } from "types/enums";
import { FormId } from "types/models.ts";

import { formSchema, ModifyOutgoingPostbackMethodsFormData } from "./formSchema";

import OutgoingPostbackData = App.Data.OutgoingPostbackData;
import StoreOutgoingPostbackConnectionSequenceStepsRequest = App.Http.Requests.Tracking.StoreOutgoingPostbackConnectionSequenceStepsRequest;

interface ApiToolGeneralFormProps {
    outgoingPostback: OutgoingPostbackData;
    onClose: () => void;
}

const ModifyPostbackOutgoingMethodsFormContent: FC<ApiToolGeneralFormProps> = ({ outgoingPostback, onClose }) => {
    const { t } = usePaldockTranslation("forms.modify-postback-outgoing-methods");
    const { t: tC } = usePaldockTranslation("common");
    const generalFormUtils = useGeneralForm<ModifyOutgoingPostbackMethodsFormData>();
    const { control, handleSubmit, fieldNames } = generalFormUtils;
    const permissionsUtils = usePermission();

    const updateOutgoingPostbackMethodsMutation = useUpdateOutgoingPostbackMethodsMutation(outgoingPostback.id ?? "");

    const onSubmitForm = (data: ModifyOutgoingPostbackMethodsFormData) => {
        const payload: StoreOutgoingPostbackConnectionSequenceStepsRequest = {
            connectionSequenceSteps: JSON.parse(data.connectionSequenceSteps),
        };

        updateOutgoingPostbackMethodsMutation.mutate(payload, {
            onSuccess: () => {
                toast.success(t("messages.update_success"));
                queryClient.refetchQueries({ queryKey: ["getOutgoingPostbackMethodsExport", outgoingPostback.id] });
                queryClient.refetchQueries({ queryKey: ["getConnectionSequenceSteps"] });
                onClose();
            },
            onError: () => {
                toast.error(t("messages.update_error"));
                control._resetDefaultValues;
            },
        });
    };

    return (
        <>
            <FormItem
                control={control}
                name={fieldNames.connectionSequenceSteps?.name || "connectionSequenceSteps"}
                label={t("fields.connection_sequence_steps.label")}
            >
                <Input.TextArea rows={4} placeholder={t("fields.connection_sequence_steps.placeholder")} />
            </FormItem>
            {/* <FormItem control={control} name={fieldNames.integration?.name || "integration"} label="Integration"> // todo edit via ace editor
                <CodeEditor id={fieldNames.integration?.name}>{control._defaultValues.integration}</CodeEditor>
            </FormItem>*/}
            {permissionsUtils.hasPermission(PermissionEnum.ToolTrackingOutgoingPostbackEditorExportSave) && (
                <Button type="primary" onClick={handleSubmit(onSubmitForm)}>
                    {tC("save")}
                </Button>
            )}
        </>
    );
};

export const ModifyPostbackOutgoingMethodsForm: FC<ApiToolGeneralFormProps> = ({ outgoingPostback, onClose }) => {
    const getOutgoingPostbackMethodsExportQuery = useGetOutgoingPostbackMethodsExportQuery(outgoingPostback?.id || "");
    const data = getOutgoingPostbackMethodsExportQuery.data;

    if (!outgoingPostback || !data) return null;

    const defaultConnectionSequenceSteps = data.connectionSequenceSteps.map((step) => ({
        ...step,
        requestConfig: { ...step.requestConfig, name: "config" },
    }));

    return (
        <GeneralFormProvider<ModifyOutgoingPostbackMethodsFormData>
            schema={formSchema}
            id={FormId.TrackingToolFromMethodsExportForm}
            defaultValues={{
                connectionSequenceSteps: JSON.stringify(defaultConnectionSequenceSteps),
            }}
        >
            <ModifyPostbackOutgoingMethodsFormContent outgoingPostback={outgoingPostback} onClose={onClose} />
        </GeneralFormProvider>
    );
};
